import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import RequirePermission from "../../../auth/RequirePermission";
import { ADMIN_PERMISSIONS } from "../../../auth/adminPermissions";
import AdminPageLayout from "../../../components/midpoint/admin/layout/AdminPageLayout";
import AdminPageHeader from "../../../components/midpoint/admin/layout/AdminPageHeader";
import AdminStatePanel from "../../../components/midpoint/admin/layout/AdminStatePanel";
import { fetchAdminDealers } from "../../../services/automapDealerOnboardingApi";
import { fetchAdminListing, updateAdminListing } from "../../../services/automapDealerInventoryApi";
import {
  AVAILABILITY_STATUSES,
  BODY_TYPE_OPTIONS,
  FUEL_OPTIONS,
  LISTING_STATUSES,
  TRANSMISSION_OPTIONS,
  formatListingStatus,
} from "../../../constants/automapDealerInventoryConstants";
import { AdminAutomotiveNav } from "../automotive/AdminAutomotiveNav";
import { ConfirmDialog, trackListingAdminEvent } from "./listingAdminShared";

const EMPTY = {
  dealerId: "",
  make: "",
  model: "",
  version: "",
  year: "",
  priceCop: "",
  mileageKm: "",
  transmission: "automatic",
  fuel: "gasolina",
  bodyType: "SUV",
  color: "",
  city: "",
  neighborhood: "",
  address: "",
  description: "",
  financingAvailable: false,
  status: "draft",
  availabilityStatus: "unknown",
  engineSize: "",
  doors: "",
  internalPlate: "",
  internalVin: "",
  soatStatus: "not_provided",
  tecnomecanicaStatus: "not_provided",
  ownershipCount: "",
  accidentHistoryNote: "",
  dealerStockNumber: "",
  externalListingUrl: "",
  rejectionReason: "",
  adminNotes: "",
  photoUrls: [""],
};

function AdminListingEditPageContent() {
  const { listingId } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [dealers, setDealers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [confirmDealerChange, setConfirmDealerChange] = useState(null);
  const [pendingDealerId, setPendingDealerId] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const [listingData, dealersData] = await Promise.all([
          fetchAdminListing(listingId),
          fetchAdminDealers({ sort: "recently_created" }),
        ]);
        if (cancelled) return;
        setDealers(dealersData.dealers || []);
        const listing = listingData.listing;
        if (!listing) throw new Error("Listing no encontrado");
        setForm({
          ...EMPTY,
          ...listing,
          year: listing.year ?? "",
          priceCop: listing.priceCop ?? "",
          mileageKm: listing.mileageKm ?? "",
          doors: listing.doors ?? "",
          ownershipCount: listing.ownershipCount ?? "",
          rejectionReason: listing.rejectionReason || "",
          adminNotes: listing.adminNotes || "",
          photoUrls: listing.photos?.length ? listing.photos.map((p) => p.url) : [""],
        });
      } catch (err) {
        setError(err.message || "No se pudo cargar el listing.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [listingId]);

  const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const buildPayload = (overrides = {}) => ({
    dealerId: form.dealerId,
    make: form.make,
    model: form.model,
    version: form.version,
    year: form.year ? Number(form.year) : null,
    priceCop: form.priceCop !== "" ? Number(form.priceCop) : null,
    mileageKm: form.mileageKm !== "" ? Number(form.mileageKm) : null,
    transmission: form.transmission,
    fuel: form.fuel,
    bodyType: form.bodyType,
    color: form.color,
    city: form.city,
    neighborhood: form.neighborhood,
    address: form.address,
    description: form.description,
    financingAvailable: form.financingAvailable,
    status: form.status,
    availabilityStatus: form.availabilityStatus,
    engineSize: form.engineSize,
    doors: form.doors ? Number(form.doors) : null,
    internalPlate: form.internalPlate,
    internalVin: form.internalVin,
    soatStatus: form.soatStatus,
    tecnomecanicaStatus: form.tecnomecanicaStatus,
    ownershipCount: form.ownershipCount ? Number(form.ownershipCount) : null,
    accidentHistoryNote: form.accidentHistoryNote,
    dealerStockNumber: form.dealerStockNumber,
    externalListingUrl: form.externalListingUrl,
    rejectionReason: form.rejectionReason,
    adminNotes: form.adminNotes,
    photos: form.photoUrls.filter(Boolean).map((url, index) => ({
      url,
      isPrimary: index === 0,
      sortOrder: index,
    })),
    ...overrides,
  });

  const save = async (overrides = {}) => {
    setSaving(true);
    setError("");
    try {
      const payload = buildPayload(overrides);
      if (payload.status === "rejected" && !payload.rejectionReason?.trim()) {
        throw new Error("Motivo de rechazo requerido");
      }
      if (payload.status === "active" && !form.photoUrls.filter(Boolean).length) {
        // warn only — server may still allow
        // eslint-disable-next-line no-console
        console.warn("Listing activo sin fotos — se recomienda al menos una.");
      }
      await updateAdminListing(listingId, payload);
      trackListingAdminEvent("admin_listing_edited", { listingId, status: payload.status });
      navigate(`/admin/listings/${listingId}`);
    } catch (err) {
      setError(err.message || "No se pudo guardar.");
    } finally {
      setSaving(false);
    }
  };

  const onDealerChange = (dealerId) => {
    if (dealerId === form.dealerId) return;
    setPendingDealerId(dealerId);
    setConfirmDealerChange(true);
  };

  if (loading) {
    return (
      <AdminPageLayout>
        <AdminStatePanel variant="empty" message="Cargando listing…" />
      </AdminPageLayout>
    );
  }

  return (
    <AdminPageLayout className="admin-listings-page">
      <AdminAutomotiveNav active="listings" />
      <AdminPageHeader
        eyebrow="AutoMapa · Listing"
        title="Editar listing"
        primaryActions={<Link className="admin-btn" to={`/admin/listings/${listingId}`}>Cancelar</Link>}
      />
      <main className="admin-console__main wrap">
        {error ? <AdminStatePanel variant="empty" message={error} /> : null}
        <form
          className="dealer-form-grid admin-listing-edit-form"
          onSubmit={(e) => {
            e.preventDefault();
            save();
          }}
        >
          <label>
            Concesionario *
            <select value={form.dealerId} onChange={(e) => onDealerChange(e.target.value)}>
              <option value="">Seleccionar</option>
              {dealers.map((d) => (
                <option key={d.id} value={d.id}>{d.businessName}</option>
              ))}
            </select>
          </label>
          <label>
            Estado
            <select value={form.status} onChange={(e) => update("status", e.target.value)}>
              {LISTING_STATUSES.map((s) => <option key={s} value={s}>{formatListingStatus(s)}</option>)}
            </select>
          </label>
          <label>
            Disponibilidad
            <select value={form.availabilityStatus} onChange={(e) => update("availabilityStatus", e.target.value)}>
              {AVAILABILITY_STATUSES.map((s) => <option key={s} value={s}>{formatListingStatus(s)}</option>)}
            </select>
          </label>
          {[
            ["make", "Marca *"],
            ["model", "Modelo *"],
            ["version", "Versión / trim"],
            ["year", "Año *"],
            ["priceCop", "Precio COP *"],
            ["mileageKm", "Kilometraje km *"],
            ["city", "Ciudad *"],
            ["neighborhood", "Barrio"],
            ["color", "Color"],
            ["dealerStockNumber", "Stock interno"],
            ["externalListingUrl", "URL externa"],
            ["internalPlate", "Placa (interno)"],
            ["internalVin", "VIN (interno)"],
          ].map(([field, label]) => (
            <label key={field}>
              {label}
              <input value={form[field]} onChange={(e) => update(field, e.target.value)} />
            </label>
          ))}
          <label>
            Transmisión
            <select value={form.transmission} onChange={(e) => update("transmission", e.target.value)}>
              {TRANSMISSION_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>
          <label>
            Combustible
            <select value={form.fuel} onChange={(e) => update("fuel", e.target.value)}>
              {FUEL_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>
          <label>
            Carrocería
            <select value={form.bodyType} onChange={(e) => update("bodyType", e.target.value)}>
              {BODY_TYPE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </label>
          <label className="full">
            Motivo de rechazo
            <input value={form.rejectionReason} onChange={(e) => update("rejectionReason", e.target.value)} />
          </label>
          <label className="full">
            Notas admin (interno)
            <textarea value={form.adminNotes} onChange={(e) => update("adminNotes", e.target.value)} />
          </label>
          <label className="full">
            Descripción
            <textarea value={form.description} onChange={(e) => update("description", e.target.value)} />
          </label>
          <label className="full">
            <input type="checkbox" checked={form.financingAvailable} onChange={(e) => update("financingAvailable", e.target.checked)} /> Financiación disponible
          </label>
          <label className="full">
            Fotos (URLs)
            {form.photoUrls.map((url, index) => (
              <input
                key={`photo-${index}`}
                value={url}
                placeholder="https://..."
                onChange={(e) => {
                  const next = [...form.photoUrls];
                  next[index] = e.target.value;
                  update("photoUrls", next);
                }}
                style={{ display: "block", width: "100%", marginTop: 4 }}
              />
            ))}
            <button type="button" className="admin-btn" onClick={() => update("photoUrls", [...form.photoUrls, ""])}>Agregar foto</button>
          </label>
        </form>

        <div className="admin-console__header-actions" style={{ marginTop: "1rem" }}>
          <button type="button" className="admin-btn" disabled={saving} onClick={() => save({ status: "draft" })}>Guardar borrador</button>
          <button type="button" className="admin-btn" disabled={saving} onClick={() => save({ status: "needs_review" })}>Marcar revisión</button>
          <button type="button" className="admin-btn admin-btn--primary" disabled={saving} onClick={() => save()}>Guardar</button>
          <button type="button" className="admin-btn admin-btn--primary" disabled={saving} onClick={() => save({ status: "active" })}>Guardar y activar</button>
          <button type="button" className="admin-btn" disabled={saving} onClick={() => save({ status: "paused" })}>Pausar</button>
          <button type="button" className="admin-btn" disabled={saving} onClick={() => save({ status: "archived" })}>Archivar</button>
        </div>
      </main>

      <ConfirmDialog
        open={confirmDealerChange}
        title="Cambiar concesionario"
        message="¿Reasignar este listing a otro concesionario?"
        onCancel={() => { setConfirmDealerChange(false); setPendingDealerId(""); }}
        onConfirm={() => {
          update("dealerId", pendingDealerId);
          setConfirmDealerChange(false);
          setPendingDealerId("");
        }}
      />
    </AdminPageLayout>
  );
}

export default function AdminListingEditPage() {
  return (
    <RequirePermission permission={ADMIN_PERMISSIONS.REAL_ESTATE_SOURCES_MANAGE}>
      <AdminListingEditPageContent />
    </RequirePermission>
  );
}
